// src/components/TotalIncomeSummary.jsx
import React from "react";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t } from "../utils/translations";
import { getThresholdUi } from "../utils/taxRules";
import {
  TrendingUp,
  Coins,
  LayoutDashboard,
  Database,
  ShieldCheck,
  Activity,
  Check,
  Package,
  Zap,
  Gem,
  Rocket,
  Crown,
  Wallet,
} from "lucide-react";
import { cn, tableTheme as T } from "./ui/analysisTableTheme.js";
import {
  backendCurrencyOrFallback,
  currencySymbolOf,
  firstFinite,
} from "../utils/fiatFields";
import { resolveUkSummaryForDisplay } from "../utils/ukSummaryTotals.js";

function expectedCurrencyForCountry(country = "DE") {
  const c = String(country || "DE").trim().toUpperCase().replace(/\s+/g, "_");
  if (["US", "USA", "UNITED_STATES"].includes(c)) return "USD";
  if (["UK", "GB", "UNITED_KINGDOM", "UNITED KINGDOM"].includes(c)) return "GBP";
  if (["CH", "CHE", "SWITZERLAND"].includes(c)) return "CHF";
  return "EUR";
}

function normalizeFiatCurrency(currency, country = "DE") {
  const c = String(currency || "").trim().toUpperCase();
  if (c && c !== "UNKNOWN" && c !== "NULL" && c !== "UNDEFINED") return c;
  return expectedCurrencyForCountry(country);
}

export default function TotalIncomeSummary({
  vet,
  vtho,
  b3tr,
  nft,
  exchange,
  liquidity,
  selectedCountry = "DE",
  selectedYear = 2025,
  isPreviewMode = false,
  previewMaskedTotals = null,
  previewTaxStatus = null,
  datasetId = null,
  planName = "Free",
  walletCount = 0,
  bvSnapshotLabel = null,
  bvSnapshotTooltip = null,
  taxChipCode = "DE",
  taxChipName = "Germany",
  taxMethod = "FIFO",
  ukTaxSummary = null,
  dataset = null,
}) {
  const { language } = useLanguage();
  const isGerman = language === "de";

  const cc = String(taxChipCode || selectedCountry || "DE").toUpperCase();

  const categoryText = (key) => {
    const map = {
      DE: {
        disposal: { en: "Disposals (§23 EStG)", de: "Veräußerungsgeschäfte (§23 EStG)" },
        other: { en: "Other income (§22 No. 3 EStG)", de: "Sonstige Einkünfte (§22 Nr. 3 EStG)" },
        native: null,
      },
      IT: {
        disposal: {
          en: "Crypto capital gains/losses (Italy: Quadro RT)",
          de: "Krypto-Plusvalenzen/Minusvalenzen (Italien: Quadro RT)",
        },
        other: {
          en: "Crypto proceeds (Staking/Airdrops/Rewards) (Italy: Quadro RT)",
          de: "Krypto-Erträge (Staking/Airdrops/Rewards) (Italien: Quadro RT)",
        },
        native: {
          disposal: "Plusvalenze e minusvalenze da cripto-attività – Quadro RT",
          other: "Altri proventi da cripto-attività (staking/ricompense) – Quadro RT",
        },
      },
      GR: {
        disposal: {
          en: "Income from capital transfer gains (Greece: E1 Table 4E)",
          de: "Einkünfte aus Kapitalübertragungsgewinnen (Griechenland: E1 Tabelle 4E)",
        },
        other: {
          en: "Other crypto income (staking/rewards/airdrops) (Greece)",
          de: "Sonstige Krypto-Einkünfte (Staking/Rewards/Airdrops) (Griechenland)",
        },
        native: {
          disposal: "Εισόδημα από υπεραξία μεταβίβασης κεφαλαίου (Ε1 Πίνακας 4Ε)",
          other: "Λοιπά εισοδήματα από κρυπτοστοιχεία (staking/rewards/airdrops)",
        },
      },
      US: {
        disposal: { en: "Capital gains/losses (Form 8949, Schedule D)", de: "Kapitalgewinne/-Verluste (Form 8949, Schedule D)" },
        other: { en: "Ordinary income (Form 1040, Schedule 1)", de: "Gewöhnliches Einkommen (Form 1040, Schedule 1)" },
        native: null,
      },
      UK: {
        disposal: {
          en: "Capital gains/losses (UK: SA108 / Cryptoasset Self Assessment)",
          de: "Kapitalgewinne/-verluste (UK: SA108 / Cryptoasset Self Assessment)",
        },
        other: {
          en: "Miscellaneous income (staking/rewards/airdrops)",
          de: "Miscellaneous income (Staking/Rewards/Airdrops)",
        },
        native: null,
      },
      GB: {
        disposal: {
          en: "Capital gains/losses (UK: SA108 / Cryptoasset Self Assessment)",
          de: "Kapitalgewinne/-verluste (UK: SA108 / Cryptoasset Self Assessment)",
        },
        other: {
          en: "Miscellaneous income (staking/rewards/airdrops)",
          de: "Miscellaneous income (Staking/Rewards/Airdrops)",
        },
        native: null,
      },
    };

    const entry = map[cc] || map.DE;
    const primary = language === "de" ? entry[key].de : entry[key].en;
    const secondary = entry.native?.[key] || null;
    return { primary, secondary };
  };

  const sectionTotals = [vet, vtho, b3tr, nft, exchange, liquidity].filter(
    (x) => x && typeof x === "object"
  );

  const pickSectionDisposalNet = (section = {}) =>
    firstFinite(
      section.disposalNetFiat,
      section.disposalNetEUR,
      section.disposalProfitFiat,
      section.disposalProfitEUR,
      section.netProfitFiat,
      section.netProfitEUR,
      section.swapProfitFiat,
      section.swapProfitEUR,
      section.swapProfit,
      0
    );

  const pickSectionOtherIncome = (section = {}) =>
    firstFinite(
      section.miscellaneousIncomeFiat,
      section.otherIncomeFiat,
      section.otherIncomeEUR,
      section.incomeFiat,
      section.incomeEUR,
      section.otherIncome,
      0
    );

  const totalSwapProfit =
    sectionTotals.reduce((sum, section) => sum + Number(pickSectionDisposalNet(section) || 0), 0) +
    (typeof nft === "number" ? Number(nft || 0) : 0) +
    (typeof exchange === "number" ? Number(exchange || 0) : 0);

  const totalOtherIncome = sectionTotals.reduce(
    (sum, section) => sum + Number(pickSectionOtherIncome(section) || 0),
    0
  );

  const isUS =
    cc === "US" ||
    cc === "USA" ||
    cc.includes("UNITED STATES");

  const isUK =
    cc === "UK" ||
    cc === "GB" ||
    cc.includes("UNITED KINGDOM") ||
    cc.includes("UNITED KINGDOM");

  const resolvedDataset = dataset || null;

  const ukSummary = isUK
    ? resolveUkSummaryForDisplay(
        {
          ...(resolvedDataset || {}),
          ukTaxSummary: ukTaxSummary || resolvedDataset?.ukTaxSummary || null,
        },
        selectedYear,
        { language, fiatCurrency: "GBP" }
      )
    : null;

  const ukSummaryHasRealValues =
    !!ukSummary &&
    (
      Number.isFinite(Number(ukSummary.disposalNetFiat)) ||
      Number.isFinite(Number(ukSummary.disposalNetEUR)) ||
      Number.isFinite(Number(ukSummary.miscellaneousIncomeFiat)) ||
      Number.isFinite(Number(ukSummary.otherIncomeFiat)) ||
      Number.isFinite(Number(ukSummary.otherIncomeEUR)) ||
      Number.isFinite(Number(ukSummary.incomeFiat)) ||
      Number.isFinite(Number(ukSummary.incomeEUR)) ||
      Number(ukSummary.disposalCount || 0) > 0
    );
  const ukTaxYearDisplay =
    ukSummary?.taxYearLabel ||
    (isUK && selectedYear
      ? `${selectedYear}/${String((Number(selectedYear) + 1) % 100).padStart(2, "0")}`
      : String(selectedYear || ""));

  const backendCurrency = backendCurrencyOrFallback(
    selectedCountry,
    vet,
    vtho,
    b3tr,
    nft,
    exchange,
    liquidity
  );

  const fiatCurrency = normalizeFiatCurrency(backendCurrency, selectedCountry);
  const fiatLocale =
    fiatCurrency === "USD"
      ? "en-US"
      : fiatCurrency === "GBP"
        ? "en-GB"
        : language === "de"
          ? "de-DE"
          : "en-US";
  const currencySymbol = currencySymbolOf(fiatCurrency);

  const formatCurrency = (value) => {
    const num = Number.isFinite(value) ? value : 0;
    try {
      return new Intl.NumberFormat(fiatLocale, {
        style: "currency",
        currency: fiatCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return `${currencySymbol} ${num.toFixed(2)}`;
    }
  };

  const normalizeMaskedValue = (value) => {
    if (!value || typeof value !== "string") return value;
    return value.replace(/[€$]/g, currencySymbol);
  };

  const maskedDisposal = previewMaskedTotals?.overall?.disposal;
  const maskedOther = previewMaskedTotals?.overall?.other;

  const summaryDisposalAmount =
    isUK && ukSummary
      ? Number(ukSummary.disposalNetFiat ?? ukSummary.disposalNetEUR ?? 0)
      : totalSwapProfit;

  const summaryOtherIncomeAmount =
    isUK && ukSummary
      ? Number(
          ukSummary.miscellaneousIncomeFiat ??
            ukSummary.otherIncomeFiat ??
            ukSummary.otherIncomeEUR ??
            ukSummary.incomeFiat ??
            ukSummary.incomeEUR ??
            0
        )
      : totalOtherIncome;

  const displayDisposalAmount =
    isPreviewMode && maskedDisposal
      ? normalizeMaskedValue(maskedDisposal)
      : formatCurrency(summaryDisposalAmount);

  const displayOtherAmount =
    isPreviewMode && maskedOther
      ? normalizeMaskedValue(maskedOther)
      : formatCurrency(summaryOtherIncomeAmount);

  const totalDisposalShortTerm =
    (typeof vet === "object"
      ? firstFinite(vet.disposalShortTermNetFiat, vet.disposalShortTermNetEUR, 0)
      : 0) +
    (typeof vtho === "object"
      ? firstFinite(vtho.disposalShortTermNetFiat, vtho.disposalShortTermNetEUR, 0)
      : 0) +
    (typeof b3tr === "object"
      ? firstFinite(b3tr.disposalShortTermNetFiat, b3tr.disposalShortTermNetEUR, 0)
      : 0) +
    (typeof nft === "object"
      ? firstFinite(nft.disposalShortTermNetFiat, nft.disposalShortTermNetEUR, 0)
      : 0) +
    (typeof exchange === "object"
      ? firstFinite(exchange.disposalShortTermNetFiat, exchange.disposalShortTermNetEUR, 0)
      : 0) +
    (typeof liquidity === "object"
      ? firstFinite(liquidity.disposalShortTermNetFiat, liquidity.disposalShortTermNetEUR, 0)
      : 0);

  const totalDisposalLongTerm =
    (typeof vet === "object"
      ? firstFinite(vet.disposalLongTermNetFiat, vet.disposalLongTermNetEUR, 0)
      : 0) +
    (typeof vtho === "object"
      ? firstFinite(vtho.disposalLongTermNetFiat, vtho.disposalLongTermNetEUR, 0)
      : 0) +
    (typeof b3tr === "object"
      ? firstFinite(b3tr.disposalLongTermNetFiat, b3tr.disposalLongTermNetEUR, 0)
      : 0) +
    (typeof nft === "object"
      ? firstFinite(nft.disposalLongTermNetFiat, nft.disposalLongTermNetEUR, 0)
      : 0) +
    (typeof exchange === "object"
      ? firstFinite(exchange.disposalLongTermNetFiat, exchange.disposalLongTermNetEUR, 0)
      : 0) +
    (typeof liquidity === "object"
      ? firstFinite(liquidity.disposalLongTermNetFiat, liquidity.disposalLongTermNetEUR, 0)
      : 0);

  const displayDisposalShortTermAmount = formatCurrency(totalDisposalShortTerm);
  const displayDisposalLongTermAmount = formatCurrency(totalDisposalLongTerm);

  const disposal = categoryText("disposal");
  const other = categoryText("other");

  const thresholdUi = getThresholdUi({
    countryCode: cc,
    year: selectedYear,
    language,
  });

  const ukAnnualExemptAmount = isUK
    ? firstFinite(
        ukSummaryHasRealValues ? ukSummary?.ukAnnualExemptAmountFiat : null,
        thresholdUi.mode === "combined" ? thresholdUi.amount : 0,
        0
      )
    : 0;
  const ukTaxableGainsAfterAllowance = isUK
    ? firstFinite(
        ukSummaryHasRealValues ? ukSummary?.ukTaxableGainsAfterAllowanceFiat : null,
        Math.max(0, summaryDisposalAmount - ukAnnualExemptAmount),
        0
      )
    : 0;
  const ukMiscIncomeAllowance = isUK
    ? firstFinite(
        ukSummaryHasRealValues ? ukSummary?.ukMiscIncomeAllowanceFiat : null,
        thresholdUi.miscAllowance,
        1000
      )
    : 0;

  const ukMiscBandLabel = (() => {
    if (!isUK) return null;
    const band = ukSummaryHasRealValues ? ukSummary?.miscIncomeReportingBand : null;
    if (band === "within_1000_misc_allowance") {
      return isGerman
        ? "Innerhalb £1.000 Miscellaneous-Income-Allowance"
        : "Within £1,000 miscellaneous income allowance";
    }
    if (band === "contact_hmrc") {
      return isGerman ? "£1.000–£2.500: HMRC kontaktieren" : "£1,000–£2,500: contact HMRC";
    }
    if (band === "self_assessment_likely") {
      return isGerman ? "Über £2.500: Self Assessment wahrscheinlich" : "Over £2,500: Self Assessment likely";
    }
    const income = summaryOtherIncomeAmount;
    if (income <= ukMiscIncomeAllowance) {
      return isGerman
        ? "Innerhalb £1.000 Miscellaneous-Income-Allowance"
        : "Within £1,000 miscellaneous income allowance";
    }
    if (income <= 2500) {
      return isGerman ? "£1.000–£2.500: HMRC kontaktieren" : "£1,000–£2,500: contact HMRC";
    }
    return isGerman ? "Über £2.500: Self Assessment wahrscheinlich" : "Over £2,500: Self Assessment likely";
  })();

  const setIdDisplay =
    datasetId != null && String(datasetId).trim() !== ""
      ? String(datasetId)
      : "-";

  const normalizePlanId = (raw) => {
    const s = (raw ?? "").toString().trim().toLowerCase();
    if (!s) return "free";
    if (s === "dev" || s.includes("dev") || s.includes("admin")) return "dev";
    if (s.includes("ultimate")) return "ultimate";
    if (s.includes("elite")) return "elite";
    if (s.includes("pro")) return "pro";
    if (s.includes("standard")) return "standard";
    if (s.includes("free")) return "free";
    return s;
  };

  function PlanBadge({ planId, size = "sm", variant = "hero", className = "", title }) {
    const id = normalizePlanId(planId);

    const styles = {
      free: {
        Icon: Check,
        bg: "from-emerald-500 via-green-600 to-lime-500",
        ring: "ring-emerald-200/70",
        glow: "from-emerald-200/60 via-lime-200/50 to-green-300/60",
      },
      standard: {
        Icon: Package,
        bg: "from-slate-500 via-slate-600 to-sky-600",
        ring: "ring-slate-200/60",
        glow: "from-slate-300/40 via-sky-300/40 to-blue-400/40",
      },
      pro: {
        Icon: Zap,
        bg: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
        ring: "ring-indigo-200/60",
        glow: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
      },
      elite: {
        Icon: Gem,
        bg: "from-amber-400 via-yellow-500 to-orange-500",
        ring: "ring-amber-200/70",
        glow: "from-amber-200/60 via-yellow-200/55 to-orange-200/55",
      },
      ultimate: {
        Icon: Rocket,
        bg: "from-red-500 via-rose-600 to-orange-500",
        ring: "ring-rose-200/60",
        glow: "from-red-300/55 via-rose-300/50 to-orange-300/50",
      },
      dev: {
        Icon: Crown,
        bg: "from-blue-600 via-indigo-600 to-violet-700",
        ring: "ring-blue-200/60",
        glow: "from-blue-300/50 via-indigo-300/45 to-violet-400/50",
      },
    };

    const cfg = styles[id] || styles.free;

    const sizes = {
      xxs: { wrap: "w-4 h-4 rounded-md", icon: "w-2.5 h-2.5", sparkle: false },
      xs: { wrap: "w-5 h-5 rounded-lg", icon: "w-3 h-3", sparkle: false },
      xsm: { wrap: "w-6 h-6 rounded-lg", icon: "w-3.5 h-3.5", sparkle: false },
      sm: {
        wrap: "w-8 h-8 rounded-xl",
        icon: "w-4 h-4",
        sparkle: true,
        sparklePos: "top-1.5 right-1.5",
        sparkleClass: "w-2 h-2",
      },
      lg: {
        wrap: "w-16 h-16 rounded-2xl",
        icon: "w-8 h-8",
        sparkle: true,
        sparklePos: "top-2 right-2",
        sparkleClass: "w-2.5 h-2.5",
      },
    };

    const s = sizes[size] || sizes.sm;
    const Icon = cfg.Icon;
    const compact = variant === "compact";

    return (
      <div
        title={title}
        className={[
          "relative inline-flex items-center justify-center overflow-hidden group",
          s.wrap,
          "bg-gradient-to-br",
          cfg.bg,
          compact ? "border border-white/20 shadow-sm" : `ring-1 ${cfg.ring} shadow-lg`,
          "transform-gpu transition-all duration-300",
          compact ? "" : "hover:shadow-xl hover:scale-[1.03]",
          className,
        ].join(" ")}
      >
        {!compact && (
          <div className={`absolute inset-0 bg-gradient-to-br ${cfg.glow} opacity-40 blur-lg`} />
        )}
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-b from-white/35 to-transparent opacity-40" />
        <div className="absolute inset-0 rounded-[inherit] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-1px_0_rgba(0,0,0,0.25)]" />
        {!compact && (
          <div className="absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/20 blur-md opacity-0 translate-x-0 group-hover:opacity-100 group-hover:translate-x-[220%] transition-all duration-700" />
        )}
        <Icon className={`${s.icon} text-white drop-shadow-sm relative z-10`} />
      </div>
    );
  }

  const normalizedPlanRaw = (planName || "Free").toString().trim();
  const normalizedPlan = normalizedPlanRaw || "Free";
  const planDisplay =
    normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1).toLowerCase();

  const flagForChip = (cc) => {
    const m = { DE: "🇩🇪", IT: "🇮🇹", GR: "🇬🇷", US: "🇺🇸", UK: "🇬🇧", GB: "🇬🇧" };
    return m[String(cc || "").toUpperCase()] || "🏳️";
  };

  const metaUi = {
    metaLabel:
      "text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500",
    metaValue: "mt-2 text-sm font-semibold text-slate-900",
    metaValueMono: "mt-2 break-all font-mono text-sm font-semibold text-slate-900",
  };

  const MetaCard = ({ icon: Icon, label, value, mono = false, children = null }) => (
    <div
      className={cn(
        "group relative overflow-hidden rounded-[1.2rem]",
        "border border-white/70 bg-white/72",
        "px-4 py-4",
        "shadow-[0_14px_34px_-26px_rgba(15,23,42,0.34)]",
        "ring-1 ring-slate-200/55",
        "backdrop-blur-[7px]",
        "transition-all duration-300",
        "hover:-translate-y-0.5 hover:bg-white/84 hover:shadow-[0_18px_38px_-24px_rgba(15,23,42,0.38)]"
      )}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-90" />
      <div className="pointer-events-none absolute -right-8 -top-8 h-20 w-20 rounded-full bg-indigo-100/45 blur-2xl" />

      {Icon ? (
        <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200/80 bg-white/80 text-slate-600 shadow-sm">
          <Icon className="h-4 w-4" />
        </div>
      ) : null}

      <div className={metaUi.metaLabel}>{label}</div>

      {children ? (
        <div className="mt-2">{children}</div>
      ) : (
        <div className={mono ? metaUi.metaValueMono : metaUi.metaValue}>{value}</div>
      )}
    </div>
  );

  const getThresholdValue = (type) => {
    if (thresholdUi.mode === "separate") {
      return type === "disposal"
        ? Number(thresholdUi.disposal?.amount || 0)
        : Number(thresholdUi.other?.amount || 0);
    }
    if (thresholdUi.mode === "combined") {
      return Number(thresholdUi.amount || 0);
    }
    return 0;
  };

  const labelTaxable = (type) => {
    if (isGerman) {
      return type === "disposal"
        ? "steuerpflichtiger Gewinn"
        : "steuerpflichtige sonstige Einkünfte";
    }
    return type === "disposal" ? "taxable profit" : "taxable other income";
  };

  const labelLoss = () => (isGerman ? "Verlust" : "loss");
  const labelNone = () => (isGerman ? "kein steuerpflichtiger Gewinn" : "no taxable profit");

  const getStatusLabel = (amount, type) => {
    const thr = getThresholdValue(type);
    if (amount < 0) return labelLoss();
    if (amount > thr) return labelTaxable(type);
    return labelNone();
  };

  const getPreviewStatusLabel = (type) => {
    const key = type === "disposal" ? "disposal" : "other";
    const s = previewTaxStatus?.[key];

    if (!s) {
      const amt = type === "disposal" ? totalSwapProfit : totalOtherIncome;
      return getStatusLabel(amt, type);
    }

    if (s.isLoss) return labelLoss();
    if (s.isTaxable) return labelTaxable(type);
    return labelNone();
  };

  const getThresholdLabel = (type) => {
    if (thresholdUi.mode === "separate") {
      return type === "disposal"
        ? thresholdUi.disposalLabel
        : thresholdUi.otherLabel;
    }
    if (thresholdUi.mode === "combined") {
      return thresholdUi.combinedLabel;
    }
    return language === "en" ? "No tax-free threshold" : "Keine Freigrenze";
  };

  const ui = {
    summaryCard:
      "group relative overflow-hidden rounded-[1.35rem] p-5 sm:p-6 text-white " +
      "shadow-[0_18px_42px_-24px_rgba(15,23,42,0.48)] ring-1 ring-white/16 " +
      "transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_46px_-22px_rgba(15,23,42,0.52)]",

    summaryIconWrap:
      "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/16 ring-1 ring-white/18 shadow-inner",

    summaryValue:
      "text-[1.65rem] sm:text-[1.85rem] font-semibold tracking-tight tabular-nums leading-none",

    summaryLabel:
      "text-[14px] sm:text-[15px] font-semibold leading-snug text-white/95",

    summarySub:
      "mt-1 text-xs leading-5 text-white/78",

    chip:
      "inline-flex items-center rounded-full bg-white/12 px-3 py-1 text-[11px] font-medium text-white/88 ring-1 ring-white/14",
  };

  return (
    <section className={cn("w-full", T.shell)}>
      <div className="relative overflow-hidden border-b border-slate-200/75 bg-[linear-gradient(180deg,rgba(248,250,252,0.96)_0%,rgba(255,255,255,0.98)_48%,rgba(246,248,255,0.96)_100%)]">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-10 top-[-3.5rem] h-40 w-40 rounded-full bg-indigo-200/25 blur-3xl" />
          <div className="absolute right-[-2.5rem] top-2 h-36 w-36 rounded-full bg-sky-200/18 blur-3xl" />
          <div className="absolute left-1/3 bottom-[-3rem] h-28 w-40 rounded-full bg-emerald-200/16 blur-3xl" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-90" />
        </div>

        <div className="relative px-5 pt-5 pb-4 sm:px-6 sm:pt-6 sm:pb-5">
          <div className="grid min-w-0 gap-3 sm:gap-4 lg:grid-cols-3">
            <MetaCard icon={Database} label="Set-ID" value={setIdDisplay} mono />
            <MetaCard icon={ShieldCheck} label={t("taxYear", language)} value={isUK ? ukTaxYearDisplay : selectedYear} />
            <MetaCard icon={Activity} label={t("plan", language)}>
              <div className="flex items-center gap-2.5">
                <PlanBadge
                  planId={planName}
                  size="xsm"
                  variant="compact"
                  className="flex-shrink-0"
                  title={planDisplay}
                />
                <span className="text-sm font-semibold text-slate-900">{planDisplay}</span>
              </div>
            </MetaCard>
          </div>

          {/* Meta-Pills: nur Chips in einer Zeile, kein zusätzlicher Kasten (wirkt sonst wie „zweite Tabelle“) */}
          <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-2.5">
            <span className={cn(T.countPill, "rounded-xl text-xs")}>
              <Wallet className="h-3.5 w-3.5 text-slate-500" />
              <span className="font-semibold text-slate-800">{t("wallets", language)}</span>
              <span className="font-semibold text-slate-900">{walletCount}</span>
            </span>

            {bvSnapshotLabel && (
              <span
                className="inline-flex items-center rounded-full border border-slate-200/90 bg-white/72 px-3 py-1 text-[11px] font-semibold text-slate-700 shadow-sm"
                title={bvSnapshotTooltip || undefined}
              >
                {bvSnapshotLabel}
              </span>
            )}

            <span
              className="inline-flex items-center rounded-full border border-emerald-200/90 bg-emerald-50/88 px-3 py-1 text-[11px] font-semibold text-emerald-800 shadow-sm"
              title="Tax logic used for this calculation"
            >
              {flagForChip(taxChipCode)} {taxChipName} ({taxChipCode})
            </span>

            <span
              className="inline-flex items-center rounded-full border border-indigo-200/90 bg-indigo-50/88 px-3 py-1 text-[11px] font-semibold text-indigo-800 shadow-sm"
              title="Cost basis / lot selection method"
            >
              {taxMethod}
            </span>
          </div>
        </div>
      </div>

      <div
        className={cn(
          T.header,
          T.headerRail,
          "before:bg-gradient-to-b before:from-indigo-500 before:to-violet-500"
        )}
      >
        <div className={T.headerInner}>
          <div className={T.headerMain}>
            <span className={T.eyebrow}>
              <LayoutDashboard className="h-3.5 w-3.5 text-indigo-600" aria-hidden />
              {language === "en" ? "Overview" : "Übersicht"}
            </span>

            <h2 className={T.title}>
              {isGerman ? "Steuerübersicht – Gesamtergebnis" : "Tax overview – overall result"}
            </h2>

            <p className={T.subtitle}>
              {isGerman
                ? "Die final berechneten Summen für Veräußerungsgeschäfte und sonstige Einkünfte."
                : "The final calculated totals for disposals and other income."}
              {" · "}
              {t("taxYear", language)}{" "}
              <span className="font-semibold tabular-nums text-slate-800">{isUK ? ukTaxYearDisplay : selectedYear}</span>
            </p>
          </div>

          <div className={T.headerAsidePills}>
            <span className="inline-flex items-center rounded-full border border-slate-200/90 bg-slate-50 px-3.5 py-[6px] text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600 shadow-sm">
              {isGerman ? "Gesamtergebnis" : "Overall result"}
            </span>
          </div>
        </div>
      </div>

      <div className="px-5 py-5 sm:px-6 sm:py-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className={`${ui.summaryCard} bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-700`}>
            <div className="relative">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className={ui.summaryLabel}>
                    {disposal.primary}
                  </h3>
                  {disposal.secondary && (
                    <span className={ui.summarySub}>
                      {disposal.secondary}
                    </span>
                  )}
                </div>

                <div className={ui.summaryIconWrap} aria-hidden>
                  <TrendingUp className="h-5 w-5 text-white" strokeWidth={2} />
                </div>
              </div>

              <p className={`${ui.summaryValue} mb-1.5`}>
                {displayDisposalAmount}
              </p>

              <p className="text-[13px] text-indigo-100/85">
                {isUK
                  ? (isGerman ? "Kapitalgewinne/-verluste gesamt" : "Capital gains/losses total")
                  : (isGerman ? "Veräußerungsgeschäfte Gesamt" : "Disposals total")}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <span className={ui.chip}>
                  {isPreviewMode ? getPreviewStatusLabel("disposal") : getStatusLabel(summaryDisposalAmount, "disposal")}
                </span>

                {getThresholdLabel("disposal") && (
                  <span className={ui.chip}>
                    {getThresholdLabel("disposal")}
                  </span>
                )}
              </div>

              {isUS && !isPreviewMode && (
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-white/10 px-4 py-3 ring-1 ring-white/15">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-100/80">
                      Short-Term
                    </div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      {displayDisposalShortTermAmount}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white/10 px-4 py-3 ring-1 ring-white/15">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-100/80">
                      Long-Term
                    </div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      {displayDisposalLongTermAmount}
                    </div>
                  </div>
                </div>
              )}

              {isUK && !isPreviewMode && (
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl bg-white/10 px-4 py-3 ring-1 ring-white/15">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-100/80">
                      {isGerman ? "Annual Exempt Amount (AEA)" : "Annual Exempt Amount (AEA)"}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      {formatCurrency(ukAnnualExemptAmount)}
                    </div>
                  </div>

                  <div className="rounded-2xl bg-white/10 px-4 py-3 ring-1 ring-white/15">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-100/80">
                      {isGerman ? "Steuerpflichtige Gains nach AEA" : "Taxable gains after AEA"}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      {formatCurrency(ukTaxableGainsAfterAllowance)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={`${ui.summaryCard} bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-800`}>
            <div className="relative">
              <div className="mb-5 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className={ui.summaryLabel}>
                    {other.primary}
                  </h3>
                  {other.secondary && (
                    <span className={ui.summarySub}>
                      {other.secondary}
                    </span>
                  )}
                </div>

                <div className={ui.summaryIconWrap} aria-hidden>
                  <Coins className="h-5 w-5 text-white" strokeWidth={2} />
                </div>
              </div>

              <p className={`${ui.summaryValue} mb-1.5`}>
                {displayOtherAmount}
              </p>

              <p className="text-[13px] text-emerald-100/85">
                {isUK
                  ? (isGerman ? "Miscellaneous income gesamt" : "Miscellaneous income total")
                  : (isGerman ? "Sonstige Einkünfte Gesamt" : "Other income total")}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <span className={ui.chip}>
                  {isPreviewMode ? getPreviewStatusLabel("other") : getStatusLabel(summaryOtherIncomeAmount, "other")}
                </span>

                {getThresholdLabel("other") && (
                  <span className={ui.chip}>
                    {getThresholdLabel("other")}
                  </span>
                )}

                {isUK && (
                  <span className={ui.chip}>
                    {isGerman
                      ? `Misc. allowance: ${formatCurrency(ukMiscIncomeAllowance)}`
                      : `Misc. allowance: ${formatCurrency(ukMiscIncomeAllowance)}`}
                  </span>
                )}

                {isUK && ukMiscBandLabel && (
                  <span className={ui.chip}>{ukMiscBandLabel}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {isUK && !isPreviewMode && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-xs leading-relaxed text-slate-600">
            {isGerman
              ? "Hinweis: V3Tax meldet steuerpflichtige Basen für VeWorld/Vechain-Aktivität. Section 104 Pooling mit Same-Day- und 30-Day-Matching. Endgültige UK-Steuer hängt von weiterem Einkommen und persönlichen Umständen ab."
              : "Note: V3Tax reports taxable bases for VeWorld/Vechain activity only. Section 104 pooling with same-day and 30-day matching. Final UK tax depends on wider income and personal circumstances."}
          </div>
        )}
      </div>
    </section>
  );
}
